import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { mockData } from '../mock';

const BoldHero = () => {
  const navigate = useNavigate();
  
  return (
    <section className="relative min-h-screen bg-jet-black overflow-hidden">
      {/* Grid Pattern Background */}
      <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
      
      {/* Gradient Overlays */}
      <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-gradient-to-br from-electric-blue/20 to-transparent blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-tl from-neon-green/20 to-transparent blur-3xl"></div>
      
      <div className="relative z-10 container mx-auto px-6 py-20">
        <div className="flex flex-col lg:flex-row items-center justify-center min-h-screen" style={{paddingTop: 'calc(5rem + 5px)'}}>
          {/* Left side - Bold Content */}
          <div className="lg:w-1/2 space-y-6 text-center lg:text-left">
            <h1 className="display-hero animate-slide-power">
              {mockData.hero.title}
            </h1>
            
            <p className="text-lg md:text-xl text-gray-300 max-w-xl animate-slide-power delay-200">
              {mockData.hero.subtitle}
            </p>
            
            {/* Power CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-slide-power delay-300">
              <button 
                onClick={() => navigate('/register')}
                className="btn-power"
              >
                {mockData.hero.ctaPrimary}
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="btn-secondary"
              >
                {mockData.hero.ctaSecondary}
              </button>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-12 animate-slide-power delay-500">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-black text-neon-green">{mockData.stats.consistency}</div>
                <div className="text-sm text-gray-400 uppercase tracking-wide">Consistency in Daily Habits</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-black text-electric-blue">{mockData.stats.focus}</div>
                <div className="text-sm text-gray-400 uppercase tracking-wide">Report Better Focus</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-black text-neon-green">{mockData.stats.breakthroughs}</div>
                <div className="text-sm text-gray-400 uppercase tracking-wide">Experience Life-Changing Breakthroughs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-black text-electric-blue">{mockData.stats.availability}</div>
                <div className="text-sm text-gray-400 uppercase tracking-wide">Coaching, Anytime, Anywhere</div>
              </div>
            </div>
          </div>

          {/* Right side - Phone Mockup with App Interface */}
          <div className="lg:w-1/2 mt-12 lg:mt-0 flex justify-center animate-slide-power delay-400">
            <div className="relative">
              {/* Subtle Glow Aura */}
              <div className="absolute inset-0 bg-gradient-to-br from-electric-blue/20 to-neon-green/20 rounded-[3rem] blur-xl scale-105"></div>
              
              {/* iPhone Mockup */}
              <div className="relative w-72 h-[600px] bg-gradient-to-br from-gray-900 to-black rounded-[3rem] shadow-2xl p-2" style={{boxShadow: '0 0 30px rgba(0, 123, 255, 0.2)'}}>
                {/* Screen */}
                <div className="w-full h-full bg-gradient-to-br from-gray-950 to-black rounded-[2.5rem] overflow-hidden relative flex flex-col">
                  {/* Status Bar */}
                  <div className="flex justify-between items-center px-4 py-2 text-white text-xs flex-shrink-0 bg-gradient-to-r from-gray-900/50 to-gray-800/50">
                    <span className="font-bold">9:41</span>
                    <div className="flex space-x-1">
                      <div className="w-4 h-2 bg-gradient-to-r from-neon-green to-electric-blue rounded-sm"></div>
                      <div className="w-1 h-2 bg-white rounded-sm"></div>
                      <div className="w-6 h-2 bg-neon-green rounded-sm"></div>
                    </div>
                  </div>

                  {/* App Header */}
                  <div className="px-4 py-4 border-b border-electric-blue/20 flex-shrink-0 bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-sm">
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <div className="w-12 h-12 bg-gradient-to-br from-electric-blue to-neon-green rounded-full flex items-center justify-center shadow-lg" style={{boxShadow: '0 0 20px rgba(57, 255, 20, 0.4)'}}>
                          <span className="text-white font-black text-lg">AI</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-white font-black text-lg tracking-wide">COACH AI</div>
                        <div className="text-neon-green text-xs flex items-center font-bold">
                          <div className="w-2 h-2 rounded-full mr-2 animate-pulse shadow-sm" style={{background: 'linear-gradient(135deg, #007BFF 0%, #39FF14 100%)', boxShadow: '0 0 8px rgba(57, 255, 20, 0.6)'}}></div>
                          READY TO TRANSFORM
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Chat Messages Container */}
                  <div className="flex-1 p-4 space-y-4 overflow-hidden bg-gradient-to-b from-transparent via-gray-950/20 to-transparent">
                    <div className="flex justify-start animate-slide-power">
                      <div className="relative max-w-[180px]">
                        <div className="bg-gradient-to-r from-electric-blue to-neon-green p-3 rounded-2xl rounded-bl-md shadow-lg" style={{boxShadow: '0 0 15px rgba(0, 123, 255, 0.3)'}}>
                          <p className="text-white font-bold text-xs leading-relaxed">READY TO CRUSH TODAY'S GOALS? 💪</p>
                          <div className="text-xs text-white/80 mt-1 font-medium">9:40 AM</div>
                        </div>
                        <div className="absolute -inset-1 bg-gradient-to-r from-electric-blue/30 to-neon-green/30 rounded-2xl blur-sm -z-10"></div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end animate-slide-power delay-100">
                      <div className="relative max-w-[180px]">
                        <div className="bg-gradient-to-r from-gray-800 to-gray-700 p-3 rounded-2xl rounded-br-md border border-gray-600 shadow-lg">
                          <p className="text-white font-medium text-xs">I need motivation for my workout</p>
                          <div className="text-xs text-gray-300 mt-1">9:41 AM</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-start animate-slide-power delay-200">
                      <div className="relative max-w-[180px]">
                        <div className="bg-gradient-to-r from-electric-blue to-neon-green p-3 rounded-2xl rounded-bl-md shadow-lg" style={{boxShadow: '0 0 15px rgba(57, 255, 20, 0.3)'}}>
                          <p className="text-white font-bold text-xs leading-relaxed">BOOM! YOU SHOWED UP! 🔥</p>
                          <div className="text-xs text-white/80 mt-1 font-medium">9:41 AM</div>
                        </div>
                        <div className="absolute -inset-1 bg-gradient-to-r from-electric-blue/30 to-neon-green/30 rounded-2xl blur-sm -z-10"></div>
                      </div>
                    </div>

                    <div className="flex justify-start animate-slide-power delay-300">
                      <div className="relative max-w-[180px]">
                        <div className="bg-gradient-to-r from-electric-blue to-neon-green p-3 rounded-2xl rounded-bl-md shadow-lg" style={{boxShadow: '0 0 15px rgba(0, 123, 255, 0.3)'}}>
                          <p className="text-white font-bold text-xs leading-relaxed">ENERGY LEVEL 1-10? ⚡</p>
                          <div className="text-xs text-white/80 mt-1 font-medium">9:42 AM</div>
                        </div>
                        <div className="absolute -inset-1 bg-gradient-to-r from-electric-blue/30 to-neon-green/30 rounded-2xl blur-sm -z-10"></div>
                      </div>
                    </div>
                  </div>

                  {/* Input Area */}
                  <div className="flex-shrink-0 p-4 bg-gradient-to-r from-gray-900 to-gray-800 border-t border-electric-blue/20 backdrop-blur-sm">
                    <div className="flex items-center space-x-3">
                      <div className="relative flex-1">
                        <input 
                          type="text" 
                          placeholder="Type your energy level..."
                          className="w-full bg-gradient-to-r from-gray-800 to-gray-700 text-white px-4 py-3 rounded-full text-xs outline-none border border-gray-600 focus:border-electric-blue/50 transition-all"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-electric-blue/5 to-neon-green/5 rounded-full opacity-0 focus-within:opacity-100 transition-opacity pointer-events-none"></div>
                      </div>
                      <button className="relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg hover:scale-110 transition-transform" style={{background: 'linear-gradient(135deg, #007BFF 0%, #39FF14 100%)', boxShadow: '0 0 20px rgba(57, 255, 20, 0.4)'}}>
                        <span className="text-white font-black text-lg">→</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-electric-blue/50 to-neon-green/50 rounded-full animate-pulse opacity-50"></div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Subtle Energy Waves */}
              <div className="absolute -inset-2 border border-neon-green/10 rounded-[3rem] animate-pulse" style={{animationDuration: '4s'}}></div>
              <div className="absolute -inset-4 border border-electric-blue/5 rounded-[3rem] animate-pulse" style={{animationDuration: '6s', animationDelay: '2s'}}></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BoldHero;